import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { isDesktopBreakpoint, mediaQueryLarge, prefersReducedMotion, preloadImage, yieldToMainThread } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

// Timing for the first-card mobile swipe hint (see #scheduleSwipeHint)
const SWIPE_HINT_DELAY = 600;
// Max time to wait for the second slide image before skipping the hint.
const SWIPE_HINT_IMAGE_TIMEOUT = 2000;

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('change', this.#handleCardQtyChange);
    this.addEventListener('pointerover', this.#preloadSwatchImages);
    window.addEventListener('pageshow', this.#handlePageShow);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });

    this.#scheduleSwipeHint();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('change', this.#handleCardQtyChange);
    this.removeEventListener('pointerover', this.#preloadSwatchImages);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.refs.slideshow?.removeEventListener('pointerdown', this.#cancelGalleryReset);
    this.#clearSwipeHintTimers();
    this.#clearGalleryResetTimers();
    // Null, not undefined, so the hint is not recomputed and still runs at most once
    if (ProductCard.#swipeHintCard === this) ProductCard.#swipeHintCard = null;
  }

  /**
   * Mirrors the card's Single/Pair selection into the quick-add form quantity,
   * so "Pair" adds two units to the cart, and shows the price for that quantity.
   * @param {Event} event
   */
  #handleCardQtyChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.dataset.cardQty != null) {
      const quantityInput = this.querySelector('.quick-add__product-form-component input[name="quantity"]');
      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.value = target.dataset.cardQty;
      }

      for (const block of this.querySelectorAll('.product-card__price [data-price-qty]')) {
        block.toggleAttribute('hidden', block.getAttribute('data-price-qty') !== target.dataset.cardQty);
      }

      this.#applyPairImage();
      this.#resetGalleryToMainSlide();
      return;
    }

    // Size pills bubble the same change event; snap those back to the main image too
    if (target.dataset.optionValueId != null) {
      this.#resetGalleryToMainSlide();
    }
  };

  /** @type {number[]} */
  #swipeHintTimers = [];

  /** @type {ProductCard | null | undefined} */
  static #swipeHintCard;

  static #firstPeekableCard() {
    if (ProductCard.#swipeHintCard !== undefined) return ProductCard.#swipeHintCard;

    const cards = /** @type {NodeListOf<ProductCard>} */ (document.querySelectorAll('product-card'));
    ProductCard.#swipeHintCard = null;
    for (const card of cards) {
      const secondSlide = card.refs?.slideshow?.slides?.[1];
      if (secondSlide && !secondSlide.hidden) {
        ProductCard.#swipeHintCard = card;
        break;
      }
    }
    return ProductCard.#swipeHintCard;
  }

  /**
   * On mobile, briefly peeks the second image of the first card that has one
   * to hint that the gallery is swipeable. Runs once on load.
   */
  #scheduleSwipeHint() {
    if (window.Shopify.designMode) return;
    if (isDesktopBreakpoint()) return;
    if (prefersReducedMotion()) return;

    const showTimer = setTimeout(() => {
      if (ProductCard.#firstPeekableCard() !== this) return;

      const { slideshow } = this.refs;
      if (!slideshow) return;

      const peek = () => {
        if (!this.isConnected) return;
        slideshow.peek();
      };

      // Wait for the second image so the peek never reveals a blank slide
      const secondImage = this.#eagerLoadSecondSlideImage();
      if (!secondImage || secondImage.complete) {
        peek();
        return;
      }

      let handled = false;
      const giveUp = setTimeout(() => {
        handled = true;
      }, SWIPE_HINT_IMAGE_TIMEOUT);
      this.#swipeHintTimers.push(giveUp);

      secondImage.addEventListener(
        'load',
        () => {
          if (handled) return;
          handled = true;
          clearTimeout(giveUp);
          peek();
        },
        { once: true }
      );
    }, SWIPE_HINT_DELAY);

    this.#swipeHintTimers.push(showTimer);
  }

  /**
   * Marks the second slide image as eager so it starts loading, and returns it.
   * @returns {HTMLImageElement | null}
   */
  #eagerLoadSecondSlideImage() {
    const image = /** @type {HTMLImageElement | null} */ (this.refs.slideshow?.slides?.[1]?.querySelector('img'));
    if (image) image.loading = 'eager';
    return image ?? null;
  }

  #clearSwipeHintTimers() {
    for (const timer of this.#swipeHintTimers) {
      clearTimeout(timer);
    }
    this.#swipeHintTimers = [];
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Update variant picker when variant:selected event fires
    const { optionValueId } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Wait for variant:update data via promise
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        // Update price, availability, and URL based on new variant
        this.updatePrice(html);
        this.updateCardAvailability(detail.resource);
        this.#updateProductUrl(html);
        this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        // Remove attribute after re-rendering since a variant selection has been made
        this.removeAttribute('data-no-swatch-selected');

        this.#updateVariantImages();

        // Force overflow list to reflow after variant update
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    const overflowLists = this.querySelectorAll('swatches-variant-picker-component overflow-list');
    if (!overflowLists.length) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      for (const overflowList of overflowLists) {
        overflowList.dispatchEvent(
          new CustomEvent('reflow', {
            bubbles: true,
            detail: {},
          })
        );
      }
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    const cardPrice = this.querySelector('.product-card__price');
    if (!cardPrice) return;

    // Cards with a Single/Pair selector hold one price block per qty. Each is
    // morphed against its counterpart so the selected qty keeps the right amount.
    const qtyBlocks = cardPrice.querySelectorAll('[data-price-qty]');
    if (qtyBlocks.length) {
      for (const block of qtyBlocks) {
        const qty = block.getAttribute('data-price-qty');
        const newBlock = html.querySelector(`product-price [data-price-qty='${qty}'] [ref='priceContainer']`);
        const container = block.querySelector(`[ref='priceContainer']`);
        if (newBlock && container) {
          morph(container, newBlock);
        }
      }
      return;
    }

    const priceContainer = cardPrice.querySelector(`[ref='priceContainer']`);
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #updateProductUrl(html) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      if (productCardLink.hasAttribute('href')) {
        productCardLink.href = productUrl;
      }
      if (cardGalleryLink instanceof HTMLAnchorElement && cardGalleryLink.hasAttribute('href')) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement && productTitleLink.hasAttribute('href')) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Swaps the card CTA between add to cart and notify me for the selected variant.
   * @param {{available?: boolean} | null | undefined} variant - The variant data of the new selection.
   */
  updateCardAvailability(variant) {
    const quickAdd = this.querySelector('.quick-add--inline');
    if (!(quickAdd instanceof HTMLElement)) return;

    quickAdd.dataset.variantAvailable = variant?.available ? 'true' : 'false';
  }

  /**
   * @typedef {object} CardVariantImage
   * @property {string} url - The image CDN url without size params.
   * @property {string} id - The media id, kept in sync with the slide-id attribute.
   * @property {string | null} [alt] - The image alt text.
   */

  /** @type {Record<string, {main?: CardVariantImage, hover?: CardVariantImage, pair?: string}> | null | undefined} */
  #variantImages;

  /**
   * Reads the per-variant image map rendered by card-gallery.
   * @returns {Record<string, {main?: CardVariantImage, hover?: CardVariantImage, pair?: string}> | null}
   */
  #variantImageData() {
    if (this.#variantImages === undefined) {
      const script = this.querySelector('script[data-card-variant-images]');
      try {
        this.#variantImages = script ? JSON.parse(script.textContent ?? '') : null;
      } catch {
        this.#variantImages = null;
      }
    }
    return this.#variantImages ?? null;
  }

  /**
   * The variant id for cards without swatch inputs, from the quick-add form.
   * @returns {string | null}
   */
  #formVariantId() {
    const input = this.querySelector('.quick-add__product-form-component input[name="id"]');
    return input instanceof HTMLInputElement ? input.value : null;
  }

  /**
   * Builds a CDN url with the display width appended.
   * @param {string} url - The image CDN url.
   * @returns {string}
   */
  #imageUrlWithWidth(url) {
    const width = window.innerWidth > 640 ? 500 : 320;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  /** @type {Set<string>} */
  #preloadedImages = new Set();

  /**
   * Warms the image cache for a hovered swatch's variant, so selecting it
   * swaps to an already-loaded image.
   * @param {Event} event
   */
  #preloadSwatchImages = (event) => {
    if (!(event.target instanceof Element)) return;

    this.#preloadPairImage();

    const label = event.target.closest('.variant-option__button-label--has-swatch');
    const input = /** @type {HTMLInputElement | null} */ (label?.querySelector('input[data-variant-id]') ?? null);
    const variantId = input?.dataset.variantId;
    const entry = variantId ? this.#variantImageData()?.[variantId] : undefined;
    if (!entry) return;

    for (const image of [entry.main, entry.hover]) {
      if (!image) continue;
      const url = this.#imageUrlWithWidth(image.url);
      if (this.#preloadedImages.has(url)) continue;
      this.#preloadedImages.add(url);
      preloadImage(url);
    }
  };

  #preloadPairImage() {
    const variantId = this.getSelectedVariantId() ?? this.#formVariantId() ?? '';
    const pairUrl = variantId ? this.#variantImageData()?.[variantId]?.pair : undefined;
    if (!pairUrl) return;

    const url = this.#imageUrlWithWidth(pairUrl);
    if (this.#preloadedImages.has(url)) return;
    this.#preloadedImages.add(url);
    preloadImage(url);
  }

  /**
   * Sets a slide's image from the variant image data.
   * @param {Element | undefined} slide - The slideshow slide.
   * @param {CardVariantImage} image - The image data to apply.
   */
  #applySlideImage(slide, image) {
    const img = /** @type {HTMLImageElement | null} */ (slide?.querySelector('img') ?? null);
    if (!img) return;

    // The stored single-qty src belongs to the previous variant, so drop it
    delete img.dataset.singleSrc;
    delete img.dataset.singleSrcset;

    img.removeAttribute('srcset');
    img.src = this.#imageUrlWithWidth(image.url);
    if (image.alt != null) img.alt = image.alt;
    slide?.setAttribute('slide-id', image.id);
  }

  /**
   * Swaps the gallery to the selected variant's images: the main slide takes the
   * variant image and the hover slide takes the variant's hover image, hiding
   * the slide when the variant has none.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    const data = this.#variantImageData();
    if (!slideshow || !data) return;

    const variantId = this.getSelectedVariantId() ?? this.#formVariantId();
    const entry = variantId ? data[variantId] : undefined;
    if (!entry?.main) return;

    // refs.slides, not the slides getter: the hover slide may be hidden and the getter filters it out
    const [mainSlide, hoverSlide] = slideshow.refs.slides ?? [];
    this.#applySlideImage(mainSlide, entry.main);

    if (hoverSlide) {
      if (entry.hover) {
        this.#applySlideImage(hoverSlide, entry.hover);
        hoverSlide.hidden = false;
      } else {
        hoverSlide.hidden = true;
      }
    }

    this.#applyPairImage();
    this.#resetGalleryToMainSlide();
  }

  /**
   * Safari's back/forward cache restores the page with the gallery still on
   * the hover slide, so snap back to the main image on restore.
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    this.#previousSlideIndex = null;
    this.#resetGalleryToMainSlide();
  };

  /**
   * Snaps the gallery back to the main slide after an option or qty change.
   * A tap can land mid-swipe, and the snap momentum then beats the immediate
   * reset, so it is re-asserted once the scroll has settled; the follow-ups
   * are no-ops when the card is already on the main slide. A fresh touch on
   * the gallery is a deliberate swipe, and a mouse over it is previewing the
   * hover image, so both skip the pending re-asserts instead of getting
   * yanked back by them; mouseleave restores the main image on its own.
   */
  #resetGalleryToMainSlide() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    slideshow.select(0, undefined, { animate: false });

    this.#clearGalleryResetTimers();
    for (const delay of [250, 800]) {
      const timer = setTimeout(() => {
        if (!this.isConnected || slideshow.current === 0 || slideshow.matches(':hover')) return;
        slideshow.select(0, undefined, { animate: false });
      }, delay);
      this.#galleryResetTimers.push(timer);
    }

    slideshow.removeEventListener('pointerdown', this.#cancelGalleryReset);
    slideshow.addEventListener('pointerdown', this.#cancelGalleryReset, { once: true });
  }

  #cancelGalleryReset = () => {
    this.#clearGalleryResetTimers();
  };

  /** @type {number[]} */
  #galleryResetTimers = [];

  #clearGalleryResetTimers() {
    for (const timer of this.#galleryResetTimers) {
      clearTimeout(timer);
    }
    this.#galleryResetTimers = [];
  }

  /**
   * Selecting Qty: Pair swaps the main image to the variant's pair image when one
   * exists; switching back to Single restores the variant image.
   */
  #applyPairImage() {
    const mainImage = /** @type {HTMLImageElement | null} */ (
      this.refs.slideshow?.slides?.[0]?.querySelector('img')
    );
    if (!mainImage) return;

    const qtyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-card-qty]:checked'));
    const isPair = qtyInput?.dataset.cardQty === '2';

    if (isPair) {
      const variantId = this.getSelectedVariantId() ?? this.#formVariantId() ?? '';
      const pairUrl = this.#variantImageData()?.[variantId]?.pair;
      if (!pairUrl) return;

      if (mainImage.dataset.singleSrc == null) {
        mainImage.dataset.singleSrc = mainImage.getAttribute('src') ?? '';
        mainImage.dataset.singleSrcset = mainImage.getAttribute('srcset') ?? '';
      }

      mainImage.removeAttribute('srcset');
      mainImage.src = this.#imageUrlWithWidth(pairUrl);
    } else if (mainImage.dataset.singleSrc) {
      mainImage.src = mainImage.dataset.singleSrc;
      if (mainImage.dataset.singleSrcset) {
        mainImage.setAttribute('srcset', mainImage.dataset.singleSrcset);
      }
      delete mainImage.dataset.singleSrc;
      delete mainImage.dataset.singleSrcset;
    } else {
      const variantId = this.getSelectedVariantId() ?? this.#formVariantId() ?? '';
      const mainUrl = this.#variantImageData()?.[variantId]?.main?.url;
      if (mainUrl) {
        mainImage.removeAttribute('srcset');
        mainImage.src = this.#imageUrlWithWidth(mainUrl);
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.hasAttribute('data-default-hover-image')) {
      slideshow.select(0, undefined, { animate: false });
      return;
    }

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(StandardEvents.productSelect, this.#handleCardProductSelect.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   * @param {ProductSelectEvent} event
   */
  #handleCardProductSelect(event) {
    // Handle URL update via promise resolution
    event.promise
      .then(() => {
        if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
          const cardLink = this.parentProductCard.refs.productCardLink;

          if (!cardLink.hasAttribute('href')) {
            this.pendingVariantId = null;
            return;
          }

          const currentUrl = new URL(cardLink.href);
          currentUrl.searchParams.set('variant', this.pendingVariantId);
          cardLink.href = currentUrl.toString();
          this.pendingVariantId = null;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    if (isSwatchInput && this.parentProductCard instanceof ProductCard) {
      const available = availableCount > 0 || clickedSwatch.dataset.optionAvailable === 'true';
      this.parentProductCard.updateCardAvailability({ available });
    }

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    // The card has one list per option (swatches, pills), so expand the one clicked
    const clickedList = event?.target instanceof Element ? event.target.closest('overflow-list') : null;
    const overflowList = clickedList ?? this.refs.overflowList;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
